import React, { useCallback } from 'react';
import { usePeersona } from '../../contexts/peersona-context';
import { usePeersonaChatBot } from '../../hooks/api/usePeersonaChat';
import { PeersonaAvatar } from './peersona-avatar';
import { PEERSONA_GRADIENT_FROM, PEERSONA_GRADIENT_TO } from './peersona-styles';

interface PeersonaTileProps {
  /** The Peersona's ChatBot id. Required — this is what `setChatbotId` opens. */
  chatbotId: number;
  /**
   * Optional pre-known peer name. When omitted, falls back to the ChatBot's
   * display_name / name from `usePeersonaChatBot`. Pass through from contexts
   * where the peer's name is already loaded (peer profile, audio tiles) to
   * avoid a render-blocking fetch.
   */
  peerName?: string;
  /**
   * Optional pre-known avatar. When omitted, falls back to the ChatBot's
   * avatar_file_path. Same rationale as `peerName`.
   */
  avatarUrl?: string | null;
  /**
   * Override the scope line. Almost never needed; the ChatBot row's
   * scope_line is the source of truth. Pass-through is here so post-audio
   * prompts can show "About what you just listened to" rather than the
   * generic scope sentence if desired.
   */
  scopeLineOverride?: string | null;
  /** Where this tile lives — purely for visual presets. */
  variant?: 'profile' | 'post-audio' | 'compact';
  /**
   * Fires before opening the panel. Use to track analytics ("opened Peersona
   * from peer profile") or to dismiss enclosing UI (e.g. close the post-audio
   * prompt before the panel slides in).
   */
  onOpen?: () => void;
  className?: string;
}

type VariantStyle = {
  wrapper: string;
  cta: string;
  /**
   * When set, the wrapper uses these Tailwind classes for its background and
   * the inline-style gradient fallback is skipped. Used by the `profile`
   * variant to render Peer-page CTAs in Kindly brand blue instead of
   * the Peersona violet gradient.
   */
  bgClass?: string;
};

const variantStyles: Record<NonNullable<PeersonaTileProps['variant']>, VariantStyle> = {
  profile: {
    wrapper: 'p-5 md:p-6 gap-3',
    cta: 'text-sm md:text-base px-5 py-2.5',
    bgClass: 'bg-button-primary hover:bg-button-primary-hover transition-colors',
  },
  'post-audio': {
    wrapper: 'p-4 gap-2',
    cta: 'text-sm px-4 py-2',
  },
  compact: {
    wrapper: 'p-3 gap-2',
    cta: 'text-xs px-3 py-1.5',
  },
};

/**
 * Tile-based entry point for opening a Peersona conversation. Drop one of
 * these into any surface (peer profile hero, post-audio overlay, collection
 * card, Fi recommendation carousel) — clicking it sets the active chatbot id
 * and opens the global Peersona panel.
 *
 * Pulls scope_line and fallback name/avatar from `usePeersonaChatBot`. When
 * the caller already has the peer's name and avatar from another fetch (e.g.
 * `PeerDetailLite`), pass them through to avoid a flash of "Peersona" text.
 */
export const PeersonaTile: React.FC<PeersonaTileProps> = ({
  chatbotId,
  peerName,
  avatarUrl,
  scopeLineOverride,
  variant = 'profile',
  onOpen,
  className,
}) => {
  const { openPanelForChatbot } = usePeersona();
  // Fetch the ChatBot meta so we can fill in scope_line / display_name /
  // avatar when the caller didn't pass them through. React Query dedupes
  // across mounts, so a profile that renders both a hero tile and a
  // post-audio tile for the same Peersona pays for the fetch only once.
  const { data: chatbot } = usePeersonaChatBot(chatbotId);

  const resolvedName = peerName ?? chatbot?.display_name ?? chatbot?.name ?? 'this peer';
  const resolvedAvatar = avatarUrl ?? chatbot?.avatar_file_path ?? null;
  const resolvedScope = (scopeLineOverride ?? chatbot?.scope_line ?? '').trim();

  const handleOpen = useCallback(() => {
    onOpen?.();
    openPanelForChatbot(chatbotId);
  }, [onOpen, openPanelForChatbot, chatbotId]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleOpen();
      }
    },
    [handleOpen],
  );

  const variantClasses = variantStyles[variant];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={onKeyDown}
      aria-label={`Talk with ${resolvedName}'s Peersona`}
      className={[
        'group relative flex w-full cursor-pointer flex-col rounded-2xl border border-transparent',
        'shadow-sm transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2',
        'focus-visible:ring-offset-2',
        variantClasses.wrapper,
        variantClasses.bgClass ?? '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={
        variantClasses.bgClass
          ? { color: 'white' }
          : {
              background: `linear-gradient(135deg, ${PEERSONA_GRADIENT_FROM} 0%, ${PEERSONA_GRADIENT_TO} 100%)`,
              color: 'white',
            }
      }
    >
      <div className="flex items-center gap-3">
        <PeersonaAvatar size={variant === 'compact' ? 32 : 40} src={resolvedAvatar} name={resolvedName} />
        <div className="min-w-0 flex-1">
          <p className="font-open-sans text-xs font-semibold uppercase tracking-[0.12em] text-white/70">Peersona</p>
          <p
            className={[
              'font-albert-sans truncate font-semibold text-white',
              variant === 'compact' ? 'text-sm' : 'text-lg md:text-xl',
            ].join(' ')}
          >
            Talk with {resolvedName}
          </p>
        </div>
      </div>

      {resolvedScope && (
        <p
          className={[
            'font-open-sans leading-5 text-white/90',
            variant === 'compact' ? 'line-clamp-2 text-xs' : 'text-sm md:text-base',
          ].join(' ')}
        >
          {resolvedScope}
        </p>
      )}

      <div className="mt-1 flex items-center justify-between gap-3">
        <span
          className={[
            'font-open-sans inline-flex items-center gap-1.5 rounded-full bg-white/15 font-semibold text-white',
            'transition-colors group-hover:bg-white/25',
            variantClasses.cta,
          ].join(' ')}
        >
          Start a conversation
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M5 12h14M13 5l7 7-7 7"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
    </div>
  );
};
